<template>
  <div class="grid-lineups">
    <div>
      <filterBar
        @updateFilters="updateFilters(...arguments)"
        :client="selectedClient"
        :fiduciary="selectedFiduciary"
        @updateProductFunds="updateProduct"
        :isGridLineup="isGridLineup"
      />
     <div v-if="loading" class="grid-lineup-filter-modal"></div>
    </div>
    <div class="grid-lineups__container">
      <mds-loader v-if="loading" />
      <div v-else class="grid-lineups__wrapper">
        <div v-if="products.length" class="grid-lineups__table">
          <div
            v-for="(product, productRow) in filteredProducts"
            :key="product.productId"
            class="grid-lineups__product"
            :id="`lineups-product-${product.productId}-${product.productCode}`"
          >
            <h3 class="grid-lineups__name">
              <CollapseButton
                :title="'Open/Close Product Funds'"
                :collapsed="product.__hidden"
                @toggle="toggleProductFunds(product.productId)"
              >
                <span>{{ product.productName }} ({{ product.productCode }})</span>
              </CollapseButton>
            </h3>

            <div class="mds-site-navigation" v-show="!product.__hidden">
              <ul class="mds-site-navigation__list">
                <li
                  v-for="(menu, menuIndex) in product.menus"
                  :key="`${product.productId}-${menu.menuId}`"
                  :class="{
                    'mds-site-navigation__list-item--active': menuIndex === product.selectedTab,
                  }"
                  class="mds-site-navigation__list-item"
                >
                  <div class="mds-site-navigation__list-item-inner">
                    <a
                      @click="selectTab(product.index, menuIndex)"
                      class="mds-button mds-button--flat mds-site-navigation__button"
                    >
                      {{ `${product.program.programName} ${menu.menuName}` }}
                      <span v-if="menu.lineupPendingForApproval" class="pending-icon mds-icon">
                        <base-grid-icon
                          class="mds-icon"
                          aria-hidden="true"
                          icon="clock-back"
                          tooltipMessage="Pending"
                          @showToolTip="showTooltip"
                          @hideToolTip="hideTooltip"
                        />
                      </span>
                    </a>
                  </div>
                </li>
              </ul>
            </div>

            <div class="grid-lineups__table grid" v-show="!product.__hidden && currentViewName === 'one-client'">
              <DataTable
                :columnsConfig="columnsConfig"
                :data="product.menus[product.selectedTab].fundsGroupedBySeries"
                class="table-container fixed-header-table"
              >
                <template v-slot:cell="{ content, row, col, sub }">
                  <template v-if="columnsConfig[col].key === 'categories'">
                    <template v-for="(category, index) in content">
                      <div :key="index">{{ category.name }}</div>
                    </template>
                  </template>
                  <template v-else-if="columnsConfig[col].key === 'approvalStatus'">
                    <status-icons
                      :item-name="statusesItemsName"
                      :status="content"
                      @showToolTip="showTooltip"
                      @hideToolTip="hideTooltip"
                    />
                  </template>
                  <template
                    v-else-if="includes(['ticker', 'secID'], columnsConfig[col].key) && sub === undefined"
                  >
                    <div
                      class="target-series fund-serie__with-series-items"
                      v-if="product.menus[product.selectedTab].fundsGroupedBySeries[row].__isTargetSeries"
                    >
                      {{ '' }}
                    </div>
                  </template>
                  <template v-else-if="columnsConfig[col].key === 'displayName' && sub === undefined">
                    <div
                      class="target-series fund-serie__with-series-items"
                      v-if="product.menus[product.selectedTab].fundsGroupedBySeries[row].subs.length > 0"
                    >
                      <!-- eslint-disable max-len -->
                      <CollapseButton
                        :title="'Open/Close Fund Series'"
                        :collapsed="product.menus[product.selectedTab].fundsGroupedBySeries[row].__hidden"
                        :data="{rowIndex: row}"
                        @toggle="toggleFundSeries(product.productId, product.menus[product.selectedTab].menuId, product.menus[product.selectedTab].fundsGroupedBySeries[row].id)"
                      >
                        <span class="fund-series-name">
                          {{ content }}
                          <mds-icon
                            class="pending-series-icon"
                            name="clock-back"
                            v-if="product.menus[product.selectedTab].fundsGroupedBySeries[row].pendingSeriesChange"
                          ></mds-icon>
                        </span>
                      </CollapseButton>
                    </div>
                    <div class="fund-series" v-else>
                      <span>{{ content }}</span>
                      <mds-tag
                        class="lcsc"
                        v-if="product.menus[product.selectedTab].fundsGroupedBySeries[row].isLCSC"
                      >
                        Lowest Cost
                      </mds-tag>
                    </div>
                  </template>
                  <template v-else-if="columnsConfig[col].key === 'displayName'">
                    <div class="target-series fund-series_item">
                      {{ content }}
                    </div>
                    <mds-tag
                      class="lcsc"
                      v-if="product.menus[product.selectedTab].fundsGroupedBySeries[row].isLCSC"
                    >
                      Lowest Cost
                    </mds-tag>
                  </template>
                  <template v-else-if="columnsConfig[col].key === 'pending'">
                    <template v-if="content">
                      <BaseGridIcon
                        class="mds-icon"
                        aria-hidden="true"
                        icon="clock-back"
                        tooltipMessage="Pending"
                        @showToolTip="showTooltip"
                        @hideToolTip="hideTooltip"
                      />
                    </template>
                    <template v-else>
                      {{ '' }}
                    </template>
                  </template>
                  <template v-else-if="columnsConfig[col].key === 'preferred'">
                    <template v-if="mustShowPreferred(productRow, columnsConfig[col].key, row, sub)">
                      <span
                        class="grid-lineups__preferred-icon"
                        role="button"
                        :class="{ star: content }"
                        @click.prevent="actionsSchema.preferred.action(row, sub)"
                      >
                        <BaseGridIcon
                          class="mds-icon grid-lineups-button__preferred"
                          aria-hidden="true"
                          :icon="product.menus[product.selectedTab].fundsGroupedBySeries[row].preferredIcon"
                          :tooltipMessage="actionsSchema.preferred.tooltipMessage"
                          @showToolTip="showTooltip"
                          @hideToolTip="hideTooltip"
                        />
                      </span>
                    </template>
                    <template v-else>
                      {{ '' }}
                    </template>
                  </template>
                  <template v-else>
                    {{ content || "—" }}
                  </template>
                </template>
              </DataTable>
            </div>

            <div class="grid-lineups__table" v-show="!product.__hidden && currentViewName === 'all-clients'">
              <DataTable
                :columnsConfig="columnsConfig"
                :data="product.menus[product.selectedTab].funds"
              >
                <template v-slot:cell="{ content, col }">
                  <div
                    class="grid-lineups__fund-status"
                    v-if="columnsConfig[col].key === 'fundStatus'"
                  >
                    <status-icons
                      :status="content"
                      :item-name="statusesItemsName"
                      @showToolTip="showTooltip"
                      @hideToolTip="hideTooltip"
                    />
                  </div>
                  <div
                    class="grid-lineups__fund-pending pending-icon mds-icon"
                    v-else-if="columnsConfig[col].key === 'isPending'"
                  >
                    <base-grid-icon
                      v-if="content"
                      class="mds-icon"
                      aria-hidden="true"
                      icon="clock-back"
                      tooltipMessage="Pending"
                      @showToolTip="showTooltip"
                      @hideToolTip="hideTooltip"
                    />
                  </div>
                  <template v-else>{{ content || "—" }}</template>
                </template>
              </DataTable>
            </div>
          </div>
        </div>
        <div v-else>
          <p v-if="selectedProduct" class="empty-state"> Lineup not available for the selected product and program type.</p>
          <p v-else class="empty-state"> To view the lineup, please select the product type from the product dropdown menu.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import {
  cloneDeep,
  get,
  merge,
  sortBy,
  find,
  includes,
  isEmpty,
  orderBy,
} from 'lodash';

import slugify from 'slugify';

import { MdsTag } from '@mds/tag';
import StatusIcons from '@/components/atoms/StatusIcons';
import BaseGridIcon from '@/components/atoms/BaseGridIcon';
import CollapseButton from '@/components/atoms/CollapseButton';
import DataTable from '@/components/molecules/data-table/table';
import FilterBar from '@/components/organisms/FilterBar';
import ErrorNotificationMixin from '@/mixins/ErrorNotificationMixin';
import GroupFundsBySeriesMixin from '@/mixins/GroupFundsBySeriesMixin';
import GridFormatDataMixin from '@/mixins/GridFormatDataMixin';

import * as LineupsFundsService from '@/services/LineupsFunds';

export default {
  name: 'grid-lineups',
  components: {
    StatusIcons,
    MdsTag,
    BaseGridIcon,
    CollapseButton,
    DataTable,
    FilterBar,
  },
  mixins: [
    ErrorNotificationMixin({ component: 'Templates/GridLineups' }),
    GridFormatDataMixin({ withGlobalState: true }),
    GroupFundsBySeriesMixin(),
  ],
  data() {
    return {
      loading: false,
      requestController: null,
      request: null,
      requestUrl: '',
      products: [],
      isGridLineup: true,
      currentFilters: {
        assets: [],
        categories: [],
        status: [],
      },
      allStatusMode: false,
      actionsSchema: {
        empty: {
          icon: '',
          action: null,
          tooltipMessage: '',
        },
        'change-status': {
          icon: 'caret-circle-down',
          action: this.changeStatus,
          tooltipMessage: 'Change Status',
        },
        'manage-series': {
          icon: 'caret-circle-down',
          action: this.manageSeries,
          tooltipMessage: 'Manage Series',
        },
      },
      columnsConfig: [
        {
          text: 'Fund Name',
          key: 'displayName',
          align: 'left',
          sortDirection: 1,
        },
        {
          text: 'Ticker',
          key: 'ticker',
          align: 'left',
          width: 124,
          sortDirection: 0,
        },
        {
          text: 'Sec ID',
          key: 'secID',
          align: 'left',
          width: 124,
          sortDirection: 0,
        },
        {
          text: 'Fiduciary Category',
          key: 'categories',
          align: 'left',
          width: 196,
          sortDirection: 0,
        },
        {
          text: 'New Sales',
          key: 'newSales',
          align: 'left',
          width: 76,
          sortDirection: 0,
        },
        {
          text: 'Status',
          key: 'approvalStatus',
          align: 'left',
          width: 76,
          sortDirection: 0,
        },
        {
          text: 'Pending',
          key: 'pending',
          align: 'left',
          width: 82,
          sortDirection: 0,
        },
        {
          text: '',
          key: 'action',
          width: 44,
        },
      ],
    };
  },
  computed: {
    ...mapState({
      policies: (state) => state.user.policies,
      selectedFiduciary: (state) => state.user.fiduciary.id,
      selectedClient: (state) => state.grid.client.selected,
      selectedProduct: (state) => state.grid.product.selected,
      selectedProductName: (state) => state.grid.product.name,
      selectedProductCode: (state) => state.grid.product.code,
      searchTerm: (state) => state.grid.searchTerm,
      selectedTab: (state) => state.grid.tab,
    }),
    filterStatusSize() {
      return this.currentFilters.status.length;
    },
    filterCategoriesSize() {
      return this.currentFilters.categories.length;
    },
    filterAssetsSize() {
      return this.currentFilters.assets.length;
    },
    statusesItemsName() {
      if (this.isOneClientView) {
        return {
          singular: 'fund',
          plural: 'funds',
        };
      }
      return {
        singular: 'client',
        plural: 'clients',
      };
    },
    currentViewName() {
      return this.selectedClient > 0 ? 'one-client' : 'all-clients';
    },
    localPolicyOptions() {
      // Tabs are applied to 1 Client View
      if (this.currentViewName === 'all-clients') {
        return get(this.policies, `grid.views.${this.currentViewName}`);
      }
      return get(this.policies, `grid.views.${this.currentViewName}.tabs.${this.selectedTab}`);
    },
    filteredProducts() {
      /* eslint-disable no-param-reassign */
      const products = cloneDeep(this.products);
      const checkFilters = this.filterStatusSize || this.filterCategoriesSize || this.filterAssetsSize;
      try {
        const filteredProducts = products
          .map((product) => {
            const { menus = [] } = product;
            product.menus = menus.map((menu) => {
              const { funds = [], fundsGroupedBySeries = [] } = menu;
              if (this.currentViewName === 'one-client') {
                menu.fundsGroupedBySeries = cloneDeep(fundsGroupedBySeries
                  .filter((fund) => {
                    let funRespFromTerm = false;
                    let fundRespFromFilter = false;
                    if (this.searchTerm.length > 0) {
                      const matchFundName = new RegExp(this.searchTerm, 'gi').test(fund.displayName);
                      const matchFundTicker = new RegExp(this.searchTerm, 'gi').test(fund.ticker);
                      const matchFundSecID = new RegExp(this.searchTerm, 'gi').test(fund.secID);
                      const matchFundCusip = new RegExp(this.searchTerm, 'gi').test(fund.cusip);

                      funRespFromTerm = matchFundName || matchFundTicker || matchFundSecID || matchFundCusip;
                    }
                    if (checkFilters) {
                      const cateroriesArr = fund.categories.map((item) => item.name);
                      const statusArr = fund.approvalStatus.map((item) => item.name);
                      let assetArr = [];
                      if (fund.morningstarBroadAssetClass?.name) {
                        assetArr.push(fund.morningstarBroadAssetClass.name);
                      } else {
                        assetArr = fund.morningstarBroadAssetClass.map((item) => item.name);
                      }
                      // eslint-disable-next-line max-len
                      const matchFoundCategory = this.currentFilters.categories.some((item) => cateroriesArr.includes(item));
                      const matchFoundStatus = this.currentFilters.status.some((item) => statusArr.includes(item));
                      const matchFoundAssets = this.currentFilters.assets.some((item) => assetArr.includes(item));
                      fundRespFromFilter = (this.currentFilters.categories.length
                        ? matchFoundCategory
                        : true)
                        && (this.currentFilters.assets.length
                          ? matchFoundAssets
                          : true)
                        && (this.currentFilters.status.length
                          ? matchFoundStatus
                          : true);
                    }

                    let fundCheckCriteria = true;
                    if (this.searchTerm.length > 0 && checkFilters === 0) {
                      fundCheckCriteria = funRespFromTerm;
                    }
                    if (this.searchTerm.length === 0 && checkFilters > 0) {
                      fundCheckCriteria = fundRespFromFilter;
                    }
                    if (this.searchTerm.length > 0 && checkFilters > 0) {
                      fundCheckCriteria = funRespFromTerm || fundRespFromFilter;
                    }
                    let showFundSeries = false;

                    if (fund.subs.length) {
                      fund.subs = orderBy(fund.subs.filter((innerrow) => {
                        let respFromTerm = false;
                        let respFromFilter = false;
                        if (this.searchTerm.length > 0) {
                          const matchName = new RegExp(this.searchTerm, 'gi').test(innerrow.displayName);
                          const matchTicker = new RegExp(this.searchTerm, 'gi').test(innerrow.ticker);
                          const matchSecID = new RegExp(this.searchTerm, 'gi').test(innerrow.secID);
                          const matchCusip = new RegExp(this.searchTerm, 'gi').test(innerrow.cusip);
                          respFromTerm = matchName || matchTicker || matchSecID || matchCusip;
                        }
                        if (checkFilters) {
                          const cateroriesArr = innerrow.categories.map((item) => item.name);
                          const statusArr = innerrow.approvalStatus.map((item) => item.name);
                          let assetArr = [];
                          if (innerrow.morningstarBroadAssetClass?.name) {
                            assetArr.push(innerrow.morningstarBroadAssetClass.name);
                          } else {
                            assetArr = innerrow.morningstarBroadAssetClass.map((item) => item.name);
                          }
                          // eslint-disable-next-line max-len
                          const matchFoundCategory = this.currentFilters.categories.some((item) => cateroriesArr.includes(item));
                          const matchFoundStatus = this.currentFilters.status.some((item) => statusArr.includes(item));
                          const matchFoundAssets = this.currentFilters.assets.some((item) => assetArr.includes(item));
                          respFromFilter = (this.currentFilters.categories.length
                            ? matchFoundCategory
                            : true)
                            && (this.currentFilters.assets.length
                              ? matchFoundAssets
                              : true)
                            && (this.currentFilters.status.length
                              ? matchFoundStatus
                              : true);
                        }

                        let innerFundCheckCriteria = true;
                        if (this.searchTerm.length > 0 && checkFilters === 0) {
                          innerFundCheckCriteria = respFromTerm;
                        }
                        if (this.searchTerm.length === 0 && checkFilters > 0) {
                          innerFundCheckCriteria = respFromFilter;
                        }
                        if (this.searchTerm.length > 0 && checkFilters > 0) {
                          innerFundCheckCriteria = respFromTerm || respFromFilter;
                        }
                        return innerFundCheckCriteria;
                      }), 'displayName');
                    }
                    if (fund.subs.length) { showFundSeries = true; }
                    return showFundSeries || fundCheckCriteria;
                  }));
              }
              if (this.currentViewName === 'all-clients') {
                menu.funds = funds
                  .map((fund) => {
                    let {
                      psaCategory = [],
                      /* eslint-disable-next-line prefer-const */
                      fundStatus: fundStatuData,
                    } = fund;
                    if (psaCategory === null) {
                      psaCategory = [{ category: { name: '', broadAssetClassName: '' } }];
                    }
                    const [firstCategory = {}] = psaCategory;
                    const { category } = firstCategory || {};
                    const { name: categoryName = '', broadAssetClassName: assetName = '' } = category || {};

                    const fundStatusObj = this.getSafeFundStatusObj(fundStatuData);
                    const { fundStatus: { name: statusName = '' } = {} } = fundStatusObj || {};

                    fund.fundStatus = [{ name: statusName }];
                    if (this.isTargetSeries(fund)) {
                      const pendingFundStatus = this.formatPendingStatuses(fund);
                      const fundActualStatus = this.formatFundStatuses(fund);
                      fund.fundStatus = this.formatTargetSeriesStatus(fund, pendingFundStatus, fundActualStatus, false);
                      fund.isPending = Array.isArray(pendingFundStatus) && pendingFundStatus.length > 0;
                    }
                    fund.categoryName = categoryName;
                    fund.assetName = assetName;

                    return fund;
                  })
                  .filter((fund) => {
                    const filter = (this.currentFilters.categories.length
                      ? this.currentFilters.categories.indexOf(fund.categoryName) !== -1
                      : true)
                      && (this.currentFilters.assets.length
                        ? this.currentFilters.assets.indexOf(fund.assetName) !== -1
                        : true)
                      && (this.currentFilters.status.length
                        ? this.currentFilters.status.indexOf(fund.statusName) !== -1
                        : true);

                    const matchName = new RegExp(this.searchTerm, 'gi').test(fund.displayName);
                    const matchTicker = new RegExp(this.searchTerm, 'gi').test(fund.ticker);
                    const matchSecID = new RegExp(this.searchTerm, 'gi').test(fund.secID);
                    const search = matchName || matchTicker || matchSecID;
                    return filter && search;
                  });
              }
              return menu;
            });
            return product;
          });
        /* eslint-enable no-param-reassign */
        return filteredProducts;
      } catch (error) {
        // handle gracefully
      }
      // If an error occurred
      return this.products;
    },
  },
  created() {
    // remove action column for one client
    if (this.currentViewName === 'one-client') {
      this.columnsConfig = this.columnsConfig.filter((item) => item.key !== 'action');
    }
  },
  methods: {
    includes,
    isEmpty,
    _load(selectedProduct) {
      /* eslint-disable no-param-reassign */
      this.loading = true;
      LineupsFundsService.getProductLineupsList({
        selectedClient: this.selectedClient,
        productId: selectedProduct.id,
      }).then((result) => {
        let productsData = [];
        if (result.status.success) {
          const { data = {} } = result;
          const { products = [] } = data;
          const productsToFilter = cloneDeep(products);
          productsData = productsToFilter.map((product, index) => {
            const { menus = [] } = product;
            product.selectedTab = 0;
            product.__hidden = false;
            product.index = index;
            product.menus = menus.map((menu) => {
              let { funds = [] } = menu;
              // Sort Menu Funds by displayName
              funds = sortBy(funds, 'displayName');
              menu.funds = this.getFundsArr(funds);
              menu.fundsGroupedBySeries = this.formatData(funds);
              return menu;
            });
            return product;
          });
        }
        this.loading = false;
        this.products = productsData;
      }).catch((error) => {
        const httpStatus = (error?.response?.status) ? (error?.response.status) : error?.status;
        if (httpStatus === 401) {
          this.$router.push('/login');
        }
        this.loading = false;
      }).finally(() => {
        this.cleanRequest();
      });
      /* eslint-enable no-param-reassign */
    },
    updateFilters(filters, allStatus) {
      this.currentFilters.assets = filters.assets;
      this.currentFilters.categories = filters.categories;
      this.currentFilters.status = filters.status;
      this.allStatusMode = allStatus;
    },
    setProduct(product, productName, productCode) {
      this.$store.dispatch('setProduct', {
        code: productCode,
        listVisible: false,
        name: productName,
        selected: product,
      });
    },
    updateProduct(product, productName, productCode) {
      if (product === 0) {
        this.setProduct(product, productName, productCode);
        this.products = [];
      } else {
        this._load(product);
      }
    },
    optionsSettingSelected(selectedOption) {
      const actionSchema = get(this.actionsSchema, selectedOption.data.actionId);
      actionSchema.action(selectedOption.data.productRow, selectedOption.data.row, selectedOption.data.subRow);
    },
    changeStatus(productRow, row, subRow) {
      const product = this.products[productRow];
      const rows = product.menus[product.selectedTab].fundsGroupedBySeries;
      let data = rows[row];
      if (undefined !== subRow) {
        data = get(rows[row], `subs[${subRow}]`, {});
      }
      this.$store.dispatch('openFullscreenModal', {
        title: 'Change Status',
        component: 'change-status',
        data,
      });
    },
    // eslint-disable-next-line
    manageSeries(productRow, row, subRow) {
      // TODO: This feature (Manage Series) will be implemented soon
      const product = this.products[productRow];
      const rows = product.menus[product.selectedTab].fundsGroupedBySeries;
      const fundData = get(rows, `[${row}]`, {});
      const fundNSeriesId = String(fundData.id).split('-');
      const fundSeriesId = fundNSeriesId[1] ?? fundData.id;
      this.$router.push({ name: 'manageSeries', params: { clientId: +this.selectedClient, seriesId: +fundSeriesId } });
    },
    /* eslint-disable no-param-reassign */
    getFundsArr(data) {
      return data.map((fund) => {
        // Update PendingStatus
        const { fundStatus, pendingStatus } = fund;
        if (pendingStatus) {
          fund.fundStatus = merge(fundStatus, pendingStatus);
        }
        return fund;
      });
    },
    formatData(data) {
      // TODO Preferred Column
      let disableNone = false;
      if (this.currentViewName === 'all-clients') {
        disableNone = true;
      } else {
        disableNone = false;
        // this.columnsConfig.splice(-3, 0, this.additionalColumns.newSales);
      }
      // Preferred column
      if (this.preferredColumn) {
        // Add Preferred column before Action column
        // this.columnsConfig.splice(-1, 0, this.additionalColumns.preferred);
      }

      this.columnsConfig = this.columnsConfig.map((col) => {
        const clonedCol = cloneDeep(col);
        if (clonedCol.sortDirection !== undefined) {
          clonedCol.action = () => {
            if (this.sortKey === clonedCol.key) {
              this.sortDirection *= -1;
            } else {
              this.sortDirection = 1;
            }
            this.sortKey = clonedCol.key;
            this.sortColumn = clonedCol.sortColumn;
            this.setSortBy();
          };
        }
        return clonedCol;
      });
      const formatedData = data.map((row, index) => {
        let newRow = {};
        try {
          const fundsSeries = get(row, 'fundSeries.seriesFunds', []);
          let fundRef = { ...row };
          if (fundsSeries && fundsSeries.length > 0) {
            fundRef = {
              ...row,
              ...find(fundsSeries, (fund) => row.id === fund.id),
            };
          }

          const fundID = row.fundID || fundRef.fundID;
          const isPreferred = row.isPreferred || fundRef.isPreferred;
          const psaCategory = row.psaCategory || [get(row, 'fundSeries.seriesCategory')];
          const morningstarBroadAssetClass = row.morningstarBroadAssetClass
            || fundRef.morningstarBroadAssetClass;
          const morningstarCategory = row.morningstarCategory || fundRef.morningstarCategory;

          const id = this.formatId(row);
          const isPending = this.formatIsPending(row);
          const pendingStatus = this.formatPendingStatuses(row);
          const fundStatus = this.formatFundStatuses(row);
          const isOpenToNewSales = this.formatOpenToNewSales(row);
          const displayName = this.formatDisplayName(row);
          const categories = this.formatCategories(psaCategory);
          const approvalStatus = this.formatApprovalStatus(
            row,
            fundStatus,
            pendingStatus,
            disableNone,
          );
          const action = this.formatActions(fundStatus, pendingStatus);
          const slug = slugify(`${displayName}-${row.secID}`);

          newRow = {
            id,
            index,
            fundID,
            slug,
            displayName,
            ticker: row.ticker,
            secID: row.secID,
            categories,
            psaCategory,
            fundStatus,
            pendingStatus,
            morningstarBroadAssetClass,
            morningstarCategory,
            approvalStatus,
            isLCSC: row.isLCSC,
            pending: isPending,
            pendingSeriesChange: row.fundSeries ? row.fundSeries.isPendingSeriesChange : false,
            action,
            newSales: isOpenToNewSales,
            preferred: isPreferred,
            subs: fundsSeries || [],
            __isTargetSeries: this.isTargetSeries(row),
            __hidden: false,
          };

          const subsData = newRow.subs;

          const updatedSubsData = subsData.map((fund) => {
            const fundStatusSub = get(fund, 'fundStatus');
            const {
              pendingStatus: pendingStatusSub,
              displayName: displayNameSub,
              secID: subSecID,
            } = fund;
            const approvalStatusSub = this.formatApprovalStatus(
              fund,
              fundStatusSub,
              pendingStatusSub,
              disableNone,
            );
            const subSlug = slugify(`${displayNameSub}-${subSecID}`);

            return {
              ...fund,
              slug: subSlug,
              fiduciaryFundID: fund.fiduciaryFundID || fund.id, // The API is unpredictable
              fundName: fund.displayName,
              preferred: fund.isPreferred,
              pending: fund.isPending,
              approvalStatus: approvalStatusSub,
              status: this.formatStatus(approvalStatusSub, fund),
              action: this.formatActions(fundStatusSub, pendingStatusSub),
              categories: this.formatCategories(fund.psaCategory),
              __isFundSeries: true,
              __hidden: false,
            };
          });

          newRow.subs = [...updatedSubsData];

          // if there is no psaCategory, replace it with morningstarCategory
          if (newRow.categories && newRow.categories.length === 0) {
            newRow.categories = [row.morningstarCategory];
          }

          if (this.preferredColumn) {
            const { canBeMarkAsPreferred, preferredIcon } = this.formatPreferred(newRow);
            newRow.canBeMarkAsPreferred = canBeMarkAsPreferred;
            newRow.preferredIcon = preferredIcon;
          }
        } catch (e) {
          // error notification
        }
        return newRow;
      });
      return formatedData;
    },
    /* eslint-enable no-param-reassign */
    showTooltip(...args) {
      this.$emit('showTooltip', ...args);
    },
    hideTooltip() {
      this.$emit('hideTooltip');
    },
    selectTab(productIndex, menuIndex) {
      this.products[productIndex].selectedTab = menuIndex;
    },
    toggleProductFunds(productId) {
      this.products = this.products.map((product) => {
        const modifProduct = cloneDeep(product);
        if (modifProduct.productId === productId) {
          modifProduct.__hidden = !modifProduct.__hidden;
        }
        return modifProduct;
      });
    },
    cleanRequest() {
      this.request = null;
      this.requestController = null;
    },
    getSafeFundStatusObj(fundStatusData) {
      if (fundStatusData instanceof String) {
        return { fundStatus: { name: fundStatusData } };
      }
      if (Array.isArray(fundStatusData) && fundStatusData.length > 0) {
        return fundStatusData[0];
      }
      if (!fundStatusData) {
        return undefined;
      }
      return fundStatusData;
    },
  },
  watch: {
    selectedClient() {
      this.products = [];
    },
  },
  beforeDestroy() {
    this.requestController && this.requestController.abort();
    this.setProduct(0, 'All Products', 'Universe');
  },
};
</script>
<style lang="scss">
.grid-lineups {
  .grid-lineup-filter-modal {
    position: absolute;
    height: 100%;
    width: 100%;
    z-index: 1000;
    top: 0;
  }
  .grid {
    // height: 1px; // Flex fix
    padding: 0 $mds-space-4-x;
    // position: relative;
  }
  .mds-data-table {
    width: 100%;
  }
  .mds-data-table__header-cell {
    vertical-align: top;
  }
  .mds-data-table__header-cell[aria-sort] {
    cursor: pointer;
  }
  .mds-data-table__header-cell-inner {
    padding-top: $mds-space-1-x;
  }
  .mds-data-table__header-cell-inner[role="button"]:focus {
    box-shadow: none;
  }
  .mds-data-table__cell {
    vertical-align: middle;
  }

  .level-0:hover,
  .level-1:hover {
    box-shadow: 0 0 4px 0 rgba(70, 60, 60, 0.08), 0 2px 4px 0 rgba(0, 0, 0, 0.08);
    .mds-data-table__cell {
      background: $mds-table-row-hover-color;
    }
  }
  .level-0:hover,
  .level-1:hover {
    &__undo .baseGridIcon {
      background-color: #e0f3fb;

      svg {
        @include mds-icon-color($mds-color-neutral-37);
      }
    }
  }
  .fund-series {
    padding: 0 $mds-space-3-x;
    // @include mds-level-5-heading();
  }
  .fund-series-name {
    font-weight: bold;
    color: $mds-color-black;
  }
  .fund-series_item {
    padding: 0 $mds-space-5-x;
    line-height: 28px;
  }
  .mds-data-table {
    tr.mds-data-table__row {
      &.parent-row,
      &.child-row {
        background-color: $mds-color-neutral-95;
      }
    }
  }

  .mds-data-table__row .baseGridIcon {
    width: $mds-space-inset-4-x;
    height: $mds-space-inset-4-x;
    border-radius: 6px;

    svg {
      @include mds-icon-color($mds-color-neutral-80);
    }
  }
}

// Action icon
.grid-lineups {
  &__action-icon:hover .baseGridIcon {
    background-color: #e0f3fb;

    svg {
      @include mds-icon-color($mds-color-neutral-37);
    }
  }
}

// Preferred icon
.grid-lineups {
  .star .baseGridIcon svg {
    @include mds-icon-color($mds-interactive-color-primary-default);
  }

  &__preferred-icon:hover .baseGridIcon {
    background-color: #e0f3fb;

      svg {
      @include mds-icon-color($mds-color-neutral-37);
    }
  }
}

// Fix Header
.fixed-header-table {
  position: relative;
}
.fixed-header-table .mds-data-table__header th {
  background-color: $mds-color-white;
}
.fixed-header-table .mds-data-table__header th {
  position: sticky;
  top: 0;
  z-index: 100;
}

// Loader
.loading-spinner {
  padding-bottom: $mds-space-2-x;
}

.title-message {
  @include mds-level-1-heading();
  font-weight: $mds-font-weight-thin;
  color: $mds-text-color-primary-on-light;
  margin: $mds-space-8-x auto;
  text-align: center;
  width: 50%;
}

.pending-series-icon{
  padding: $mds-space-three-quarter-x 0 0 $mds-space-half-x;
}

.grid-lineups {
  flex: 1 1 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  min-height: 0; // Firefox Flexbox fix

  &__container {
    height: 100%;
    overflow-y: auto;
  }

  &__product {
    h3 {
      @include mds-level-3-heading();
      margin-bottom: 0;
    }

    &:last-child {
      margin-bottom: $mds-space-inset-8-x;
    }
  }

  .mds-site-navigation {
    margin-bottom: $mds-space-3-x;
    &__list {
      justify-content: flex-start;
    }
    &__list-item {
      margin: 0;
    }
    &__list-item--active {
      .mds-site-navigation__list-item-inner {
        box-shadow: inset 0 -6px 0 0 #061c25;
      }
    }
    .pending-icon {
      padding-left: $mds-space-1-x;
    }
  }

  &__fund-status {
    padding-left: $mds-space-inset-1-x;
  }

  &__fund-pending {
    padding-left: $mds-space-inset-2-x;
  }

  &__table {
    padding-left: $mds-space-2-x;

    .mds-data-table__cell {
      color: $mds-color-neutral-20;
      font-size: $mds-font-size-s;
      padding-bottom: $mds-space-inset-2-x;
      padding-top: $mds-space-inset-2-x;
    }
  }

  .empty-state {
    font-family: $mds-font-family-sans-serif;
    font-size: $mds-font-size-m;
    padding-left: $mds-space-2-x;
  }

  @media screen and (max-height: 624px) {
    .single-list, .multiple-list {
      max-height: 250px;
    }
  }
}
</style>

<style lang="scss" module>
:global(.lcsc) {
  &.mds-tag {
    margin-left: $mds-space-1-x;
  }
}
</style>
